import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
const SOLID_COLORS = [
    '#1E1E26',
    '#3B3B4F',
    '#5A5A6E',
    '#9E9EB0',
    '#FFFFFF',
    '#2CC5B8',
    '#7B61FF',
    '#EF5B5B',
    '#F5C542',
    '#4ECB71',
];
const GRADIENT_PRESETS = [
    'linear-gradient(135deg, #1E1E26 0%, #3B3B4F 100%)',
    'linear-gradient(135deg, #3B3B4F 0%, #6B6B80 100%)',
    'linear-gradient(135deg, #5A7A9E 0%, #8EAABF 100%)',
    'linear-gradient(135deg, #8EC5E0 0%, #D0ECF5 100%)',
    'linear-gradient(135deg, #B0D8E8 0%, #E8F4F8 100%)',
    'linear-gradient(135deg, #1E3A8A 0%, #7B61FF 100%)',
    'linear-gradient(135deg, #7B61FF 0%, #2CC5B8 100%)',
    'linear-gradient(135deg, #2CC5B8 0%, #4ECDC4 100%)',
    'linear-gradient(135deg, #F5C542 0%, #2CC5B8 100%)',
    'linear-gradient(135deg, #EF5B5B 0%, #F5C542 100%)',
];
export function ChangeBackgroundPopup({ currentBackground, onSelectBackground, onUploadImage, onClose, inline = false, }) {
    const [hexInput, setHexInput] = useState(currentBackground?.startsWith('#') ? currentBackground : '#1E1E1E');
    const fileInputRef = useRef(null);
    const previewColor = currentBackground?.startsWith('#')
        ? currentBackground
        : hexInput;
    const handleHexSubmit = useCallback(() => {
        const cleaned = hexInput.trim();
        if (/^#(?:[0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(cleaned)) {
            onSelectBackground(cleaned);
        }
    }, [hexInput, onSelectBackground]);
    const handleUploadImage = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            onUploadImage(file);
            onClose();
        }
        e.target.value = '';
    }, [onUploadImage, onClose]);
    return (_jsxs(PopupRoot, { "$inline": inline, onClick: (e) => e.stopPropagation(), children: [_jsxs(Section, { children: [_jsx(SectionLabel, { children: "Solid" }), _jsx(SwatchGrid, { children: SOLID_COLORS.map((color) => (_jsx(SwatchButton, { "$selected": currentBackground === color, onClick: () => onSelectBackground(color), children: _jsx(Swatch, { style: { background: color } }) }, color))) })] }), _jsxs(Section, { children: [_jsx(SectionLabel, { children: "Gradient" }), _jsx(SwatchGrid, { children: GRADIENT_PRESETS.map((gradient) => (_jsx(SwatchButton, { "$selected": currentBackground === gradient, onClick: () => onSelectBackground(gradient), children: _jsx(Swatch, { style: { background: gradient } }) }, gradient))) })] }), _jsx(Divider, {}), _jsxs(HexRow, { children: [_jsx(SwatchButton, { "$selected": false, children: _jsx(Swatch, { style: { background: previewColor } }) }), _jsx(HexInput, { type: "text", value: hexInput, onChange: (e) => setHexInput(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                handleHexSubmit();
                        }, onBlur: handleHexSubmit })] }), _jsx(Divider, {}), _jsxs(UploadButton, { onClick: handleUploadImage, children: [_jsx(UploadIconSvg, {}), _jsx("span", { children: "Upload image" })] }), _jsx(HiddenFileInput, { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleFileChange })] }));
}
function UploadIconSvg() {
    return (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": "Upload image", children: [_jsx("rect", { x: "3", y: "3", width: "14", height: "14", rx: "2", stroke: "currentColor", strokeWidth: "1.5" }), _jsx("path", { d: "M10 13V7M10 7L7.5 9.5M10 7l2.5 2.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
const PopupRoot = styled.div `
    display: flex;
    flex-direction: column;
    padding: ${({ $inline }) => ($inline ? '0' : '12px')};
    border-radius: ${({ $inline }) => ($inline ? '0' : '16px')};
    border: ${({ $inline }) => ($inline ? 'none' : '1px solid rgba(255, 255, 255, 0.4)')};
    background: ${({ $inline }) => ($inline ? 'none' : 'rgba(64, 64, 64, 0.3)')};
    backdrop-filter: ${({ $inline }) => ($inline ? 'none' : 'blur(50px)')};
    width: ${({ $inline }) => ($inline ? '100%' : '285px')};
    box-sizing: border-box;
`;
const Section = styled.div `
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
`;
const SectionLabel = styled.div `
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    color: #ededf2;
`;
const SwatchGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(5, 36px);
    gap: 10px 17.5px;
    justify-content: start;
`;
const SwatchButton = styled.button `
    width: 36px;
    height: 36px;
    padding: 0;
    border: 2px solid
        ${({ $selected }) => $selected ? 'rgba(123, 97, 255, 1)' : 'transparent'};
    border-radius: 50%;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 150ms ease;

    &:hover {
        border-color: rgba(255, 255, 255, 0.5);
    }
`;
const Swatch = styled.div `
    width: 30px;
    height: 30px;
    border-radius: 50%;
`;
const Divider = styled.div `
    height: 1px;
    background: rgba(255, 255, 255, 0.12);
    margin: 0 0 12px 0;
`;
const HexRow = styled.div `
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
`;
const HexInput = styled.input `
    flex: 1;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: rgba(57, 56, 56, 0.6);
    color: #ededf2;
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    padding: 0 8px;
    outline: none;
    box-sizing: border-box;

    &:focus {
        background: rgba(57, 56, 56, 0.8);
    }
`;
const UploadButton = styled.button `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: rgba(57, 56, 56, 0.6);
    color: #ededf2;
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    cursor: pointer;
    transition: background 150ms ease;

    &:hover {
        background: rgba(57, 56, 56, 0.8);
    }

    svg {
        flex-shrink: 0;
    }
`;
const HiddenFileInput = styled.input `
    display: none;
`;
